/**
 * A custom MCP server as one row of the integration catalogue: it sits among
 * the connected integrations, looks like them, and opens its own page. Only
 * the server's host and how many of its tools Eve may use set it apart.
 *
 * @module components/apps/integrations/CustomMcpServerRow
 */
import { Link } from "@tanstack/react-router";
import { ChevronRightIcon, WebhookIcon } from "lucide-react";

import { cn } from "~/lib/utils";
import type { CustomMcpServer } from "../../../state/customMcpServers";
import { allowedToolsLabel, customMcpHost } from "./customMcpServerContent";

/** Whole-word searches that mean "the custom MCP servers" rather than one of them. */
const KIND_QUERY = /^(?:custom|mcp|custom mcp|mcp servers?|custom mcp servers?)$/;

/** Whether a server matches a page's search box (an already lower-cased, trimmed query). */
export const customMcpServerMatches = (server: CustomMcpServer, query: string): boolean =>
  query.length === 0 ||
  server.name.toLowerCase().includes(query) ||
  server.url.toLowerCase().includes(query) ||
  KIND_QUERY.test(query);

/** The status pill: the server passed its connection check, so it is connected unless switched off. */
export const customMcpServerStatus = (server: Pick<CustomMcpServer, "enabled">) =>
  server.enabled
    ? { label: "Connected", className: "bg-success/10 text-success" }
    : { label: "Off", className: "bg-muted text-muted-foreground" };

/** The tile every custom server shares: no vendor artwork, a webhook glyph (the MCP mark). */
export function CustomMcpServerIcon({ className }: { readonly className?: string | undefined }) {
  return (
    <span
      className={cn(
        "flex shrink-0 items-center justify-center rounded-xl border border-border/60 bg-background",
        className,
      )}
    >
      <WebhookIcon className="size-1/2 text-muted-foreground" />
    </span>
  );
}

export function CustomMcpServerRow({
  server,
  orgSegment,
}: {
  readonly server: CustomMcpServer;
  readonly orgSegment: string;
}) {
  const params = { org: orgSegment, serverId: server.id };
  const status = customMcpServerStatus(server);
  return (
    <li className="group relative flex items-center gap-3 rounded-2xl px-3 py-3 transition-colors hover:bg-muted/50">
      <Link
        to="/$org/apps/custom/$serverId"
        params={params}
        className="absolute inset-0 rounded-2xl outline-none ring-ring focus-visible:ring-2"
        aria-label={`Open ${server.name}`}
      />
      <CustomMcpServerIcon className="size-10" />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
          <span className="truncate text-sm font-medium text-foreground">{server.name}</span>
          <span className={cn("shrink-0 rounded-full px-2 py-0.5 text-[11px]", status.className)}>
            {status.label}
          </span>
        </div>
        <p className="truncate text-sm text-muted-foreground">
          Custom MCP server at{" "}
          <span className="font-mono text-xs">{customMcpHost(server.url)}</span>
          <span aria-hidden className="mx-1.5">
            ·
          </span>
          {allowedToolsLabel(server.tools)}
        </p>
      </div>
      <ChevronRightIcon className="size-4 shrink-0 text-muted-foreground/50 transition-colors group-hover:text-muted-foreground max-sm:hidden" />
    </li>
  );
}
